// REUSABLE: Data Table Component
// Generic table component with loading, empty states, and click handlers. Used by: All list/table views (Timesheet, Tasks, Leave, Reports, etc.)
// Benefits: Consistent table styling across app, DRY - no repetitive table markup, Built-in loading and empty states, Follows existing design patterns

import React from 'react';
import { Center } from '@mantine/core';

export interface ColumnDef<T = any> {
  header: string; // Column header text
  headerClassName?: string; // CSS class for header cell
  cellClassName?: string; // CSS class for body cells in this column
  cell: (row: T, index: number) => React.ReactNode; // Render function for cell content
  minWidth?: string; // Minimum width for column
}

interface DataTableProps<T = any> {
  data: T[]; // Array of data to display
  columns: ColumnDef<T>[]; // Column definitions
  loading?: boolean; // Loading state
  error?: string | null; // Error message
  emptyMessage?: string; // Message to show when no data
  onRowClick?: (row: T, index: number) => void; // Optional row click handler
  getRowClassName?: (row: T, index: number) => string; // Optional function to determine row className
  getRowKey?: (row: T, index: number) => string | number; // Optional function to determine row key
  tableClassName?: string; // Table container className
  showRowNumbers?: boolean; // Show row numbers
}

// Reusable data table component
export function DataTable<T = any>({
  data,
  columns,
  loading = false,
  error = null,
  emptyMessage = 'No records available.',
  onRowClick,
  getRowClassName,
  getRowKey,
  tableClassName = '',
  showRowNumbers = false,
}: DataTableProps<T>) {
  
  // Loading state
  if (loading) {
    return (
      <div className="text-center py-10">
        <span className="indicator-progress" style={{ display: 'block' }}>
          Please wait...
          <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
        </span>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="mb-lg-15 alert alert-danger">
        <div className="alert-text font-weight-bold">{error}</div>
      </div>
    );
  }

  // Build columns with optional row numbers
  const finalColumns = showRowNumbers
    ? [
        {
          header: 'Sr.No',
          headerClassName: 'text-center',
          cellClassName: 'text-center',
          cell: (_: T, index: number) => index + 1,
          minWidth: '80px',
        } as ColumnDef<T>,
        ...columns,
      ]
    : columns;

  return (
    <div className="table-responsive">
      <table
        className={`table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4 table-bordered ${tableClassName}`}
      >
        <thead>
          <tr className="fw-bold text-muted">
            {finalColumns.map((col, idx) => (
              <th
                key={idx}
                className={col.headerClassName || ''}
                style={col.minWidth ? { minWidth: col.minWidth } : undefined}
              >
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        
        {data.length > 0 ? (
          <tbody className="text-gray-800 fw-semibold">
            {data.map((row, rowIndex) => {
              const rowKey = getRowKey
                ? getRowKey(row, rowIndex)
                : (row as any).id || rowIndex;
              
              const rowClass = getRowClassName
                ? getRowClassName(row, rowIndex)
                : '';
              
              const cursorStyle = onRowClick ? 'pointer' : 'default';

              return (
                <tr
                  key={rowKey}
                  className={rowClass}
                  onClick={() => onRowClick?.(row, rowIndex)}
                  style={{ cursor: cursorStyle }}
                >
                  {finalColumns.map((col, colIndex) => (
                    <td key={colIndex} className={col.cellClassName || ''}>
                      {col.cell(row, rowIndex)}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        ) : (
          <tbody>
            <tr>
              <td colSpan={finalColumns.length}>
                <Center>{emptyMessage}</Center>
              </td>
            </tr>
          </tbody>
        )}
      </table>
    </div>
  );
}

export default DataTable;


